import TextSnippet from "@mui/icons-material/TextSnippet";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ManagementTableAuthorComponent from "components/shared/ManagementTableAuthorComponent";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import {
    checkSimilarTermExists,
    transferWorkLabel,
    transferWorkPath,
} from "containers/TransferTermFormContainer/utils";
import { getCountries } from "countries";
import { formatLocaleDateString, formatNumber } from "helpers/utils";
import { t } from "i18next";
import type { Dispatch, SetStateAction } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { ITransferTermSale } from "store/transferTermSalesStore";
import type { ITransferTerm } from "store/transferTermsStore";
import { ETransferTermStatus } from "store/transferTermsStore";
import type { TFetchStatus } from "store/types";

import type { IStatusRule } from "../../../components/shared/StatusChip";
import TermStatusChip from "../../../components/shared/StatusChip";
import { getListOfCountries } from "../utils";
import styles from "./style.module.scss";

interface ITransferPaymentsTable {
    /**
     * All transferTerms.
     */
    transferTerms: ITransferTerm[];
    /**
     * All transferTermSales (needed for the TransferTermDrawer).
     */
    transferTermSales: ITransferTermSale[];
    /**
     * Status of the getAll transferTerms.
     */
    termsFetchStatus: TFetchStatus;
    /**
     * Data and functions from useAPIPagination needed for LastElementTable.
     */
    APIData: any;
    /**
     * Count of all transferTerms from getAll.
     */
    termsCount: number;
    /**
     * Function to toggle TransferTermDrawer.
     */
    handleToggleDrawer: (value: boolean, transferTerm?: ITransferTerm | number) => Promise<void>;
    /**
     * Term selected (either for an action or to toggle drawer)
     */
    currentTerm: ITransferTerm | undefined;
    setCurrentTerm: Dispatch<SetStateAction<ITransferTerm | undefined>>;
    /**
     * Handle search query
     */
    handleQuery?: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Status options for the status column and StatusChip component.
 */
export const termStatusRules = (): Record<any, IStatusRule> => {
    return {
        [ETransferTermStatus.UP_TO_DATE]: {
            color: "green",
            text: t("TransferTerms.TermsStatus.Active"),
        },
        [ETransferTermStatus.TO_SIGN]: {
            color: "blue",
            text: t("TransferTerms.TermsStatus.ToSign"),
        },
        [ETransferTermStatus.UNDER_NEGOTIATION]: {
            color: "yellow",
            text: t("TransferTerms.TermsStatus.Negotiating"),
        },
        [ETransferTermStatus.EXPIRED_SOON]: {
            color: "red",
            text: t("TransferTerms.TermsStatus.EndsSoon"),
        },
        [ETransferTermStatus.EXPIRED]: {
            color: "yellow",
            text: t("TransferTerms.TermsStatus.Finished"),
        },
    };
};

/**
 * Within TransferTerms page, second tab which displays a line per term (a term links a transferee with a title).
 */
const TransferTermsTable = ({
    transferTerms,
    transferTermSales,
    termsFetchStatus,
    APIData,
    termsCount,
    handleToggleDrawer,
    setCurrentTerm,
    currentTerm,
    handleQuery,
}: ITransferPaymentsTable) => {
    const [t] = useTranslation();
    const navigate = useNavigate();

    /**
     * Actions available on the ThreeDotMenuButton.
     */
    const actionOptions: ThreeDotMenuOptions[] = [
        {
            id: "1",
            name: t("TransferTerms.TermTable.SeeTermDetails"),
            action: () => handleToggleDrawer(true, currentTerm),
        },
        {
            id: "2",
            name: t("TransferTerms.TermTable.Renegociate"),
            action: () => navigate("/transfer-terms/new", { state: { term: currentTerm } }),
        },
    ];

    const formatCountriesColumn = (countriesCodes: string[]) => {
        const countriesNames = getListOfCountries(countriesCodes, getCountries());

        return <span>{countriesNames}</span>;
    };

    const formatActionsColumn = (currentRow: ITransferTerm) => {
        return <ThreeDotMenu options={actionOptions} onMenuClick={() => setCurrentTerm(currentRow)} />;
    };

    const formatAdvanceColumn = (term: ITransferTerm) => {
        const termAdvances = transferTermSales.filter(
            (payment) => payment.type === "advance" && term.id === payment.transferTermId,
        );

        const total =
            termAdvances.filter((item) => item.status === 6).reduce((a, { netAmount }) => a + netAmount, 0) / 100;

        return formatNumber(total, { isCurrency: true });
    };

    const formatTitleColumn = (_titleName: string, transferTerm: ITransferTerm) => {
        const workPath = transferWorkPath(transferTerm);
        return (
            <div className={styles["title-cell"]}>
                <span
                    className={styles["title-name"]}
                    onClick={() => {
                        if (workPath) navigate(workPath);
                    }}
                >
                    {transferWorkLabel(transferTerm)}
                </span>
                {!!transferTerm.isDeleted && (
                    <span className={styles["delete-info"]}>{t("TransferTerms.TermTable.TermDeleted")}</span>
                )}
            </div>
        );
    };

    const paymentsColumns: LastElementTableColumns[] = [
        {
            id: "transfereeName",
            label: t("TransferTerms.TermTable.Transferee"),
            sort: true,
            align: "left",
            format: (value, id, currentRow) => {
                const termsForComparison = transferTerms.filter((item) => item.id !== currentRow.id);
                const similarTermExists = checkSimilarTermExists(
                    currentRow.countryCodes,
                    termsForComparison,
                    currentRow.workId,
                    currentRow.transfereeId,
                    "table",
                );

                return (
                    <ManagementTableAuthorComponent
                        author={currentRow?.transfereeName ?? ""}
                        avatarUrl={currentRow.transfereeAvatarUrl}
                        beneficiaryId={currentRow.transfereeId}
                        termReference={
                            similarTermExists && currentRow.reference
                                ? currentRow.reference
                                : similarTermExists && !currentRow.reference
                                  ? currentRow.id
                                  : ""
                        }
                        page="transfer-terms"
                        handleQuery={handleQuery}
                    />
                );
            },
        },
        {
            id: "workName",
            sort: true,
            align: "left",
            label: t("TransferTerms.TermTable.Title"),
            format: (value, id, currentRow) => formatTitleColumn(value, currentRow),
        },
        {
            id: "countryCodes",
            align: "left",
            label: t("TransferTerms.TermTable.Country"),
            format: (value) => formatCountriesColumn(value),
        },
        {
            id: "advancePayment",
            align: "right",
            label: t("TransferTerms.TermTable.AdvancePayment") + t("TransferTerms.TermTable.Received"),
            format: (value, id, currentRow) => formatAdvanceColumn(currentRow),
        },
        {
            id: "totalGrossAmount",
            label: t("TransferTerms.TermTable.RoyaltiesCreated"),
            sort: true,
            align: "right",
            format: (value) => formatNumber(value / 100, { isCurrency: true }),
        },
        {
            id: "totalNetAmount",
            label: t("TransferTerms.TermTable.RoyaltiesPaid"),
            sort: true,
            align: "right",
            format: (value) => formatNumber(value / 100, { isCurrency: true }),
        },
        {
            id: "expirationDate",
            label: t("TransferTerms.TermTable.EndContract"),
            sort: true,
            align: "right",
            format: (value) => formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
        },
        {
            id: "status",
            label: t("TransferTerms.TermTable.Status"),
            minWidth: "200px",
            align: "left",
            format: (value) => <TermStatusChip status={value} statusRules={termStatusRules()} />,
        },
        { id: "actions", label: "", format: (value, id, currentRow) => formatActionsColumn(currentRow) },
    ];

    return (
        <>
            <a
                className={styles["helpdesk-link"]}
                href="https://helpdesk.crealo.app/fr/article/le-tableau-des-contrats-de-cessions-15p40xp/"
                target="_blank"
                rel="noreferrer"
            >
                {t("TransferTerms.Helpdesk.TableDoc")}
                <TextSnippet />
            </a>
            <LastElementTable
                columns={paymentsColumns}
                data={transferTerms}
                max={-1}
                APIData={APIData}
                dataCount={termsCount}
                dataType="transferTerms"
                onRowClick={(id, id2, type, row: ITransferTerm) => handleToggleDrawer(true, row)}
                isLoading={termsFetchStatus === "pending"}
                columnsNumber={9}
            />
        </>
    );
};

export default React.memo(TransferTermsTable);
